import { useCallback, useEffect, useMemo } from 'react';
import { useGqlQuery } from '@API/useAvQuery';
import { datadogRum } from '@datadog/browser-rum';
import { QueryKey } from '@fabric-ui/utils';
import { useNavigate } from '@utils/AvRouter';
import { initializePendo } from '@utils/pendo/pendo.utils';
import { shouldRecordSession } from '@utils/rum.utils';
import { gql } from 'graphql-request';
import kfuseRumSDK from 'kf-browser-sdk';
import { AccountType } from './AvContext.types';
import { AccountDto } from './types';

export const GET_ACCOUNT = gql`
  query findStartupAccountByContext {
    findStartupAccountByContext {
      name
      logo
      accountType
      createdAt
      autoScheduleHour
      apps {
        id
        name
      }
      aiFeatures {
        enabled
      }
    }
  }
`;

const GET_RECOMMENDATION_SETTINGS = gql`
  query getRecommendationSettings {
    getRecommendationSettings {
      enabled
    }
  }
`;

const defaultAccount = {
  id: '',
  name: '',
  apps: [],
  logo: null,
  autoScheduleHour: null,
  accountType: AccountType.POC,
  createdAt: '',
  flags: { isRecommendedFieldsEnabled: false },
  aiFeatures: { enabled: true },
};

type AccountData = Omit<AccountDto, 'flags'> & { createdAt: string };

interface RecommendationSettingsData {
  enabled: boolean;
}

export const useAccount = (options?) => {
  const navigate = useNavigate();

  const {
    data: accountData,
    isPending: isAccountLoading,
    error: accountError,
    refetch: refetchAccount,
  } = useGqlQuery<AccountData>({ queryKey: [QueryKey.account], query: GET_ACCOUNT, ...options });

  const {
    data: recommendationSettingsData,
    isPending: isSettingsLoading,
    error: recommendationSettingsError,
    refetch: refetchSettings,
  } = useGqlQuery<RecommendationSettingsData>({
    queryKey: [QueryKey.recommendationSettings],
    query: GET_RECOMMENDATION_SETTINGS,
    ...options,
  });

  useEffect(() => {
    if (accountError) {
      console.error(accountError);
      // @ts-ignore
      if (accountError.response?.errors?.[0].extensions?.status === 404) {
        navigate('error-process-request');
      }
    }
  }, [accountError]);

  const data = useMemo(() => {
    if (!accountData || !recommendationSettingsData) {
      return undefined;
    }

    return {
      ...accountData,
      flags: { isRecommendedFieldsEnabled: recommendationSettingsData.enabled },
    };
  }, [accountData, recommendationSettingsData]);

  useEffect(() => {
    if (data && shouldRecordSession) {
      datadogRum.setUser({
        ...datadogRum.getUser(),
        accountName: data.name,
        accountType: data.accountType,
        accountCreated: data.createdAt,
      });

      kfuseRumSDK.setUser({
        ...datadogRum.getUser(),
        accountName: data.name,
        accountType: data.accountType,
        accountCreated: data.createdAt,
      });

      initializePendo(datadogRum.getUser() as any);
    }
  }, [data]);

  return {
    data: data || defaultAccount,
    isPending: isAccountLoading || isSettingsLoading,
    error: accountError || recommendationSettingsError,
    refetch: useCallback(() => Promise.all([refetchAccount(), refetchSettings()]), [refetchAccount, refetchSettings]),
  };
};
